import React, {useEffect, useState} from "react";
import {useParams, useLocation, useNavigate} from "react-router-dom";
import {Row, Col, Button, Alert} from "reactstrap";
import ReactGA from "react-ga4";
import {BackgroundColorContext} from "../contexts/BackgroundColorContext";
import {useUser} from "../contexts/AuthContext";
import {GoogleAnalyticsEventCategories} from "../constants";
import PageHeader from "../components/Common/PageHeader";
import AnalysisResults from "../components/AdvancedAnalysis/AnalysisResults";
import AuthenticationRequired from "../components/Auth/AuthenticationRequired";
import {RingLoader} from "react-spinners";
import {AnalysisResult} from "../types/advancedAnalysis";
import {createNewAnalysis, getCachedAnalysis, getCacheAge} from "../services/advancedAnalysisService";
import toast from "react-hot-toast";

interface LocationState {
  analysisResult?: AnalysisResult;
}

const AdvancedAnalysisView: React.FC = () => {
  const {logId} = useParams<{logId: string}>();
  const location = useLocation();
  const navigate = useNavigate();
  const {user, isInitialized} = useUser();
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userId: string = user.identityId as string;

  useEffect(() => {
    // Track page view
    ReactGA.event({
      category: GoogleAnalyticsEventCategories.ANALYSIS,
      action: "advanced analysis page viewed",
      label: logId
    });
  }, [logId]);

  useEffect(() => {
    const loadAnalysisResult = async () => {
      if (!isInitialized) {
        return;
      }

      if (!user.authenticated) {
        setLoading(false);
        return;
      }

      if (!logId) {
        setError("No log ID provided");
        setLoading(false);
        return;
      }

      // Check if result was passed via location state
      const state = location.state as LocationState;
      if (state?.analysisResult) {
        setAnalysisResult(state.analysisResult);
        setLoading(false);
        return;
      }

      // Otherwise, try to get cached analysis result
      try {
        setLoading(true);
        const cached = getCachedAnalysis(userId, logId);
        if (cached) {
          setAnalysisResult(cached);
          setError(null);
        } else {
          setError("No analysis found. Please run analysis from the Files page.");
        }
      } catch (err: any) {
        //console.error("Failed to load analysis result:", err);
        setError(err.response?.data?.error?.message || "Failed to load analysis result");
        toast.error("Failed to load analysis result");
      } finally {
        setLoading(false);
      }
    };

    loadAnalysisResult();
  }, [isInitialized, user.authenticated, userId, logId, location.state]);

  const handleReanalyze = async () => {
    if (!logId || !user.jwtToken) {
      return;
    }

    try {
      setLoading(true);
      // Re-analyze always creates fresh analysis (consumes 1 token)
      const result = await createNewAnalysis(userId, logId, user.jwtToken);
      setAnalysisResult(result);
      setError(null);
      toast.success("Analysis completed successfully!");

      // Track reanalysis
      ReactGA.event({
        category: GoogleAnalyticsEventCategories.ANALYSIS,
        action: "reanalysis performed",
        label: logId
      });
    } catch (err: any) {
      //console.error("Reanalysis failed:", err);
      const errorMessage = err.response?.data?.error?.message || "Reanalysis failed";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToFiles = () => {
    navigate("/files");
  };

  // Show loading spinner
  if (loading) {
    return (
      <div className="iconWrapper">
        <RingLoader color="#4682b4" size={80} loading={true} />
      </div>
    );
  }

  // Check if user is authenticated
  if (isInitialized && !user.authenticated) {
    return (
      <AuthenticationRequired
        featureName="advanced analysis"
        message="You must be signed in to view advanced analysis results."
      />
    );
  }

  // Show error state
  if (error) {
    return (
      <BackgroundColorContext.Consumer>
        {() => (
          <>
            <PageHeader
              title="Advanced Analysis"
              subtitle="AI-powered performance analysis and recommendations"
              icon="fa fa-chart-line"
            />
            <Row>
              <Col md="12">
                <Alert color="danger">
                  <h4 className="alert-heading">
                    <i className="fa fa-exclamation-triangle mr-2"></i>
                    Error Loading Analysis
                  </h4>
                  <p>{error}</p>
                  <hr />
                  <div className="mb-0">
                    <Button color="primary" onClick={handleBackToFiles}>
                      <i className="fa fa-arrow-left mr-2"></i>
                      Back to Files
                    </Button>
                  </div>
                </Alert>
              </Col>
            </Row>
          </>
        )}
      </BackgroundColorContext.Consumer>
    );
  }

  // Show analysis results
  const cacheAgeMinutes = analysisResult && logId ? getCacheAge(userId, logId) : null;

  return (
    <BackgroundColorContext.Consumer>
      {() => (
        <>
          <PageHeader
            title="Advanced Analysis"
            subtitle="AI-powered performance analysis and recommendations"
            icon="fa fa-chart-line"
          />
          <Row className="mb-3">
            <Col>
              <Button color="secondary" size="sm" onClick={handleBackToFiles}>
                <i className="fa fa-arrow-left mr-2"></i>
                Back to Files
              </Button>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              {analysisResult ? (
                <AnalysisResults
                  result={analysisResult}
                  cacheAgeMinutes={cacheAgeMinutes}
                  onReanalyze={handleReanalyze}
                  layoutMode="standalone"
                />
              ) : (
                <Alert color="info">
                  <i className="fa fa-info-circle mr-2"></i>
                  No analysis result available. Please run analysis from the Files page.
                </Alert>
              )}
            </Col>
          </Row>
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default AdvancedAnalysisView;
